import RestaurantPageContent from '@components/restaurant-page-content';
import React from 'react';
import { convertToSlug } from '@utils/convert-to-slug';
import { logger } from '@utils/logger';
import { find, get, isEmpty, map, toLower } from 'lodash';
import gql from 'graphql-tag';
import { fetch } from '@content/queries';
import { ContentMatrix } from '@components/content-matrix';
import { seomaticChildQuery } from '@content/query-snippets';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import PropTypes from 'prop-types';
import { extractSeoMetaFromEntry, seoPropDefaults, seoPropTypes } from '@utils/extract-seo-meta-from-entry';
import { observer } from 'mobx-react';
import { withAppProps } from '@utils/with-app-props';
import { useFeature } from '@hooks/use-feature';
import { Layout } from '../../../components/layout';
import { RestaurantJsonLd } from '../../../components/restaurant-jsonLd';

const RestaurantPage = ({ metaContent, restaurant, restaurantPageContent }) => {
  const showCmsRestaurantPages = useFeature('show-cms-restaurant-pages');
  return (
    <Layout metaContent={metaContent} hasBackgroundPattern={showCmsRestaurantPages}>
      <RestaurantJsonLd restaurant={restaurant} />
      <RestaurantPageContent restaurant={restaurant} restaurantPageContent={restaurantPageContent} />
    </Layout>
  );
};

const getRestaurantMapping = async () => {
  const data = await fetch({
    query: gql`
      query getRestaurantNames {
        restaurants {
          id
          name
          address {
            state
          }
        }
      }
    `,
  });

  return get(data, 'restaurants', []);
};

export const getStaticPaths = async () => {
  try {
    const restaurantMapping = await getRestaurantMapping();

    return {
      paths: map(restaurantMapping, ({ name, address }) => ({
        params: { state: convertToSlug(address.state), restaurant: convertToSlug(name) },
      })),
      fallback: 'blocking',
    };
  } catch (error) {
    // The restaurant listing API (RedCat-backed) is intermittently unreliable. If it fails
    // during the build, don't fail the whole deploy — skip pre-rendering and let every
    // restaurant page be generated on-demand on first request (fallback: 'blocking'), then
    // cached and revalidated as normal.
    logger.error('Failed to fetch restaurant list for static paths; falling back to on-demand generation.', {
      error,
    });

    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};
export const getStaticProps = withAppProps(async context => {
  const { restaurant: restaurantSlug } = context.params;

  let restaurantMapping;
  try {
    restaurantMapping = await getRestaurantMapping();
  } catch (error) {
    // Transient failure of the RedCat-backed listing API while (re)generating this page.
    // Return a short-lived 404 so the build/deploy doesn't fail and Next re-attempts
    // generation on the next request, rather than caching a hard error.
    logger.error('Failed to fetch restaurant list while generating static page; will retry on next request.', {
      restaurantSlug,
      error,
    });
    return {
      notFound: true,
      revalidate: 60, // retry generation in 1 minute
    };
  }

  const simpleRestaurant = find(restaurantMapping, restaurant => restaurantSlug === convertToSlug(restaurant.name));

  if (isEmpty(simpleRestaurant)) {
    return {
      notFound: true,
      revalidate: 60 * 5, // recheck for newly-added restaurants in 5 minutes
    };
  }

  const contentQuery = gql`
    query getStaticRestaurantDataById {
      restaurant(id: "${simpleRestaurant.id}") {
        id
        name
        longitude
        latitude
        phone
        address {
          address1
          address2
          country
          postcode
          state
          suburb
        }
        catering
        facilities
        deliveryServices {
          uberEats {
            live
            link
          }
          menulog {
            live
            link
          }
          doorDash {
            live
            link
          }
        }
        utcOffset
        seoTitle
        openingHours {
          day
          openingTime
          closingTime
        }
      }
    }
  `;

  // get both the restaurant and the default template with id 0, so we can use default if no restaurant specific page exists
  const cmsQuery = gql`
    query getRestaurantByIdFromCMS {
      entries(section: "restaurantPages", restaurantId: ["${simpleRestaurant.id}", 0], siteId: "${process.env.cms.siteId}") {
        ... on restaurantPages_Entry {
          slug
          title
          restaurantId
          heading
          subheading
          description
          body
          ${ContentMatrix.query}
          ${seomaticChildQuery}
        }
      }
    }
  `;

  const [restaurantData, restaurantPages] = await Promise.all([
    fetch({
      query: contentQuery,
    }).catch(error => {
      // Transient RedCat/API failure for this specific restaurant. Swallow it so the build
      // doesn't fail; the empty result triggers the notFound + revalidate branch below,
      // which lets Next regenerate the page on a later request.
      logger.error('Failed to fetch restaurant data while building static page; will retry on next request.', {
        restaurantSlug,
        id: simpleRestaurant.id,
        error,
      });
      return {};
    }),
    fetch({
      query: cmsQuery,
    }).catch(() => {
      return { entries: [] };
    }),
  ]);

  const { restaurant } = restaurantData;

  const defaultCmsPageContent = find(restaurantPages.entries, { restaurantId: 0 });

  const restaurantCmsPageContent = find(restaurantPages.entries, { restaurantId: Number(simpleRestaurant.id) });

  const cmsPageContent = restaurantCmsPageContent ?? defaultCmsPageContent;

  if (isEmpty(restaurant)) {
    logger.error('Error fetching restaurant data while building static page.', {
      restaurantSlug,
      id: simpleRestaurant.id,
      restaurant,
    });
    return {
      notFound: true,
      revalidate: 60, // retry generation in 1 minute
    };
  }

  /* uri from CMS is invalid as we use restaurant data to compile URL and cannot
  reference the state in the default template. hence we swap cms uri for SEO purposes */
  const metaContent = cmsPageContent
    ? extractSeoMetaFromEntry(cmsPageContent, {
        searchUri: `/restaurants/${cmsPageContent.slug}`,
        replaceUri: `/restaurants/${toLower(restaurant.address.state)}/${convertToSlug(restaurant.name)}`,
        searchVariable: '$restaurantName',
        replaceVariable: restaurant.name,
      })
    : // if there is no seo meta content available in CMS, default to old behaviour
      {
        additionalMetaTags: [
          {
            key: 'description',
            data: {
              name: 'description',
              content: `Contact details, location, store hours, and directions to Nando’s ${restaurant.seoTitleString ??
                restaurant.name} Restaurant. Visit us in-store or order online!`,
            },
          },
        ],
        metaTitleOverride: [
          {
            key: 'title',
            data: {
              title: `Nando's | ${restaurant.seoTitleString ?? restaurant.name} Restaurant`,
            },
          },
        ],
      };

  return {
    props: {
      restaurant,
      restaurantPageContent: cmsPageContent ?? null,
      metaContent,
    },
    revalidate: 60 * 5, // 5 minutes
  };
});

RestaurantPage.propTypes = {
  metaContent: PropTypes.shape(seoPropTypes),
  restaurantPageContent: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    restaurantId: PropTypes.number.isRequired,
    heading: PropTypes.string,
    subheading: PropTypes.string,
    description: PropTypes.string,
    body: PropTypes.string,
    contentMatrix: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }),
  restaurant: restaurantPropTypes.isRequired,
};

RestaurantPage.defaultProps = {
  metaContent: seoPropDefaults,
  restaurantPageContent: null,
};

export default observer(RestaurantPage);
